import './DeleteCompanyModal.scss';
import React, {useContext, useState} from 'react';
import {TextField} from '@mui/material';
import {get} from 'lodash';
import {I18nReactContext} from '../../../../contexts/I18nContext';
import {Modal} from '../../../Smart/Modal/Modal';
import {Button} from '../../../Form/Button/Button';
import {buttonColors} from '../../../Pickers/InputTheme';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';
import {SA_deleteCompanyAPI} from '../../../../Services/SA_api';

/**
 * Type-to-confirm modal for permanently deleting a company.
 * The confirm button stays disabled until the typed value matches the
 * company's name or identifier exactly.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onDeleted - Called after a successful deletion
 * @param {object} props.company - The company row ({id, companyName, identifier})
 */
export const DeleteCompanyModal = ({open, onClose, onDeleted, company}) => {
    const {i18n} = useContext(I18nReactContext);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [hasError, setHasError] = useState(false);

    const companyName = get(company, 'companyName', '');
    const identifier = get(company, 'identifier', '');
    // Require an EXACT match (no trimming): padded, partial, or differently-cased
    // input must keep the confirm button disabled so a company cannot be deleted by accident.
    const isMatch = confirmText.length > 0 && (confirmText === companyName || (!!identifier && confirmText === identifier));

    const handleClose = () => {
        setConfirmText('');
        setHasError(false);
        onClose();
    };

    const handleConfirm = async () => {
        setIsDeleting(true);
        setHasError(false);
        try {
            await SA_deleteCompanyAPI({companyId: get(company, 'id'), confirmationName: confirmText});
            onDeleted();
        } catch (error) {
            console.error('Failed to delete company:', error);
            setHasError(true);
        } finally {
            setIsDeleting(false);
        }
    };

    return <Modal open={open} onClose={handleClose}>
        <div className="DeleteCompanyModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('superAdmin.customersTable.deleteCompany.title')}: {companyName}</div>
            </div>
            <div className="modalContent">
                {isDeleting ? <LoadingSpinner local/> : <>
                    <div className="deleteCompany__warning">
                        {i18n.translate('superAdmin.customersTable.deleteCompany.warning')}
                    </div>
                    <div className="deleteCompany__backupNote">
                        {i18n.translate('superAdmin.customersTable.deleteCompany.backupNote')}
                    </div>
                    <div>{i18n.translate('superAdmin.customersTable.deleteCompany.confirmLabel')}</div>
                    <TextField
                        className="deleteCompany__input"
                        variant="outlined"
                        size="small"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={i18n.translate('superAdmin.customersTable.deleteCompany.confirmPlaceholder')}
                        inputProps={{'aria-label': i18n.translate('superAdmin.customersTable.deleteCompany.confirmLabel')}}
                    />
                    {hasError && <div className="deleteCompany__error">
                        {i18n.translate('superAdmin.customersTable.deleteCompany.error')}
                    </div>}
                </>}
            </div>
            <div className="modalFooter">
                <Button color={buttonColors.GREY}
                        text={i18n.translate('superAdmin.customersTable.deleteCompany.cancel')}
                        onClick={handleClose}
                        disabled={isDeleting}/>
                <Button color={buttonColors.YELLOW}
                        text={i18n.translate('superAdmin.customersTable.deleteCompany.confirm')}
                        onClick={handleConfirm}
                        disabled={!isMatch || isDeleting}/>
            </div>
        </div>
    </Modal>;
};
